import React from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';

import {
  BudgetConsolidationSource,
  BudgetControlAreaSummary,
} from './budgetconsolidation.models';


const money = (value: any) =>
  Number(value || 0).toLocaleString('fr-FR', {
    maximumFractionDigits: 0,
  });


interface Props {
  rows: BudgetControlAreaSummary[];
  sources?: BudgetConsolidationSource[];

  disabled?: boolean;

  onReturnSource?: (
    source: BudgetConsolidationSource
  ) => void;
}


export default class BudgetControlSummaryPanel extends React.PureComponent<Props> {

  private sourceFor = (
    code: string
  ): BudgetConsolidationSource | undefined => {

    return (this.props.sources || []).find(
      (source) =>
        String(source.sourcecode || '').toUpperCase() ===
        String(code || '').toUpperCase()
    );
  };


  private lifecycleColor = (
    statecode?: string
  ):
    | 'default'
    | 'success'
    | 'warning'
    | 'error'
    | 'info' => {

    switch (String(statecode || '').toUpperCase()) {

      case 'SUBMITTED':
        return 'success';

      case 'RETURNED':
        return 'warning';

      case 'DRAFT':
        return 'default';

      case 'NOT_STARTED':
        return 'default';

      default:
        return 'info';
    }
  };


  render() {

    const {
      rows,
      disabled,
      onReturnSource,
    } = this.props;

    if (!rows?.length) {
      return null;
    }


    return (
      <Grid container spacing={2}>

        {rows.map((row) => {

          const source =
            this.sourceFor(row.controlareacode);

          const lifecycle =
            source?.statecode || 'NOT_STARTED';

          const canReturn =
            Boolean(
              source?.canreturn &&
              source?.stamp &&
              onReturnSource
            );


          return (
            <Grid
              key={row.controlareaid || row.controlareacode}
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <Card
                elevation={1}
                sx={{
                  height: '100%',
                }}
              >
                <CardContent>

                  <Stack spacing={1.5}>

                    {/* HEADER */}

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                        }}
                      >
                        {row.controlareaname}
                      </Typography>

                      <Chip
                        size="small"
                        label={
                          source?.statename ||
                          lifecycle.replaceAll('_', ' ')
                        }
                        color={
                          this.lifecycleColor(lifecycle)
                        }
                        variant={
                          source?.authoritative
                            ? 'filled'
                            : 'outlined'
                        }
                      />
                    </Stack>


                    {/* AUTHORITY */}

                    <Typography
                      variant="caption"
                      color={
                        source?.authoritative
                          ? 'success.main'
                          : 'text.secondary'
                      }
                    >
                      {source?.authoritative
                        ? 'Authoritative for Consolidation'
                        : lifecycle === 'RETURNED'
                          ? 'Returned for correction'
                          : 'Preliminary / not yet authoritative'}
                    </Typography>


                    {/* FINANCIAL VALUES */}

                    <Stack spacing={0.5}>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Ceiling
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          {money(row.ceilingamount)}
                        </Typography>
                      </Stack>


                      <Stack
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Consolidated
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          {money(
                            row.approvedamount ??
                            row.requestedamount
                          )}
                        </Typography>
                      </Stack>


                      <Stack
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Available
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          {money(row.availableamount)}
                        </Typography>
                      </Stack>

                    </Stack>


                    {/* UTILISATION */}

                    <Box>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{ mb: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Utilisation
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {Number(
                            row.utilisationpercent || 0
                          ).toFixed(1)}
                          %
                        </Typography>
                      </Stack>

                      <LinearProgress
                        variant="determinate"
                        value={Math.max(
                          0,
                          Math.min(
                            100,
                            Number(
                              row.utilisationpercent || 0
                            )
                          )
                        )}
                      />

                    </Box>


                    {/* CONTROL STATUS */}

                    {row.controlstatus && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Control: {row.controlstatus}
                      </Typography>
                    )}


                    {/* RETURNED REASON */}

                    {lifecycle === 'RETURNED' &&
                      source?.returnreason && (

                        <Typography
                          variant="caption"
                          color="warning.main"
                        >
                          {source.returnreason}
                        </Typography>

                      )}


                    {/* TARGETED GOVERNANCE ACTION */}

                    {canReturn && (

                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={
                            <UndoOutlinedIcon />
                          }
                          disabled={disabled}
                          onClick={() =>
                            onReturnSource?.(source!)
                          }
                        >
                          Return
                        </Button>
                      </Stack>

                    )}

                  </Stack>

                </CardContent>
              </Card>
            </Grid>
          );

        })}

      </Grid>
    );
  }
}